import { DialogActionsBar } from "@tql/ui-components-react/kendo/dialogs";
import type { FormRenderProps } from "@tql/ui-components-react/kendo/form";
import { Loader } from "@tql/ui-components-react/kendo/indicators";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { Form, FormElement } from "@tql/ui-components-react/tql/form/beta";
import { useState } from "react";
import { z } from "zod";

import { TermsConsent } from "~/features/my-settings/index.tsx";
import {
	FieldType,
	ValidationInputField,
} from "~/shared/components/fields/validation-input-field.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import type { TraxUser } from "~/shared/schemas/trax-user-schema.ts";

// import { getAllowedRoles } from "~/shared/schemas/trax-user-schema.ts";
import styles from "./add-user-modal.module.css";

export type VendorUserFormData = {
	firstName: string;
	lastName: string;
	email: string;
	// role: string;
};

type AddEditUserModalProps = {
	// readonly currentUserRole: string;
	readonly onClose: () => void;
	readonly onSubmit: (data: VendorUserFormData) => Promise<void>;
	readonly user?: TraxUser;
	readonly isPending?: boolean;
};

const emailSchema = z
	.string()
	.min(1, { message: "Email is required." })
	.email({ message: "Please enter a valid email address." });

const emailValidator = (value: string) => {
	const result = emailSchema.safeParse(value ?? "");
	if (!result.success) {
		return (
			result.error.issues[0]?.message ?? "Please enter a valid email address."
		);
	}
	return "";
};

const requiredValidator = (value: string) => {
	return value ? "" : "This field is required.";
};

export const AddEditUserModal = ({
	// currentUserRole,
	onClose,
	onSubmit,
	user,
	isPending,
}: AddEditUserModalProps) => {
	const isEditMode = !!user;
	const isMobile = useIsMobile();
	const [consentError, setConsentError] = useState(false);
	const [noChangesError, setNoChangesError] = useState(false);

	// const roleDropdownData = getAllowedRoles(currentUserRole);

	const rowClassName = isMobile
		? `${styles.row} ${styles.rowMobile}`
		: styles.row;

	const handleSubmit = async (dataItem: Record<string, unknown>) => {
		const consent = dataItem["userConsent"] as boolean | undefined;
		if (!consent) {
			setConsentError(true);
			return;
		}
		setConsentError(false);

		const firstName = dataItem["firstName"] as string;
		const lastName = dataItem["lastName"] as string;
		const email = dataItem["email"] as string;

		if (
			isEditMode &&
			user.firstName === firstName &&
			user.lastName === lastName
		) {
			setNoChangesError(true);
			return;
		}
		setNoChangesError(false);

		await onSubmit({ firstName, lastName, email });
	};

	return (
		<Form
			initialValues={
				isEditMode
					? {
							firstName: user.firstName,
							lastName: user.lastName,
							email: user.email,
							permissionLevel: user.role,
						}
					: undefined
			}
			onSubmit={handleSubmit}
			render={(formRenderProps: FormRenderProps) => (
				<TraxModal
					cancelText={"Cancel"}
					className={
						isMobile
							? `${styles.addUserModalForm} ${styles.addUserModalFormMobile}`
							: styles.addUserModalForm
					}
					confirmText={isEditMode ? "Save Changes" : "Add User"}
					onCancel={onClose}
					onClose={onClose}
					showButtonsDisabled={isPending}
					showCancelButton={true}
					showConfirmationButtons={false}
					title={isEditMode ? "Edit a User" : "Add a User"}
					width={isMobile ? undefined : 500}
				>
					{isPending ? (
						<div className={styles.userLoader}>
							<Loader
								size={"medium"}
								themeColor={"primary"}
								type={"infinite-spinner"}
							/>
							{isEditMode ? "Updating User" : "Adding User"}
						</div>
					) : (
						<FormElement id={"add-edit-user-form"}>
							<div className={styles.formContent}>
								<div className={rowClassName}>
									<ValidationInputField
										fieldType={FieldType.INPUT}
										id={"firstName"}
										label={"First Name"}
										name={"firstName"}
										required={true}
										validator={requiredValidator}
									/>
									<ValidationInputField
										fieldType={FieldType.INPUT}
										id={"lastName"}
										label={"Last Name"}
										name={"lastName"}
										required={true}
										validator={requiredValidator}
									/>
								</div>
								<div className={rowClassName}>
									<ValidationInputField
										disabled={isEditMode}
										fieldType={FieldType.INPUT}
										id={"email"}
										label={"Email"}
										name={"email"}
										required={true}
										type={"email"}
										validator={emailValidator}
									/>
									{/* TODO: Added only when there are more than 1 third party role */}
									{/* <ValidationInputField
										data={roleDropdownData}
										fieldType={FieldType.DROPDOWN}
										id={"permissionLevel"}
										label={"Permission Level"}
										name={"permissionLevel"}
										required={true}
										validator={requiredValidator}
									/> */}
								</div>
								<TermsConsent />
								{consentError ? (
									<div className={styles.errorMessage}>
										Please check the box acknowledging your consent before
										submitting.
									</div>
								) : null}
								{noChangesError ? (
									<div className={styles.errorMessage}>
										Please make changes to the form before you submit changes.
									</div>
								) : null}
							</div>
						</FormElement>
					)}

					<DialogActionsBar layout={"end"}>
						<Button disabled={isPending} onClick={onClose} variant={"tertiary"}>
							Cancel
						</Button>
						<Button
							disabled={!formRenderProps.allowSubmit || isPending}
							onClick={formRenderProps.onSubmit}
							variant={"primary"}
						>
							{isEditMode ? "Save Changes" : "Add User"}
						</Button>
					</DialogActionsBar>
				</TraxModal>
			)}
		/>
	);
};
